import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode } from 'react';
import * as authApiClient from '../services/authApiClient';
import * as creditApiClient from '../services/creditApiClient';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<any>;
  loginWithGoogle: (token: string, referralCode?: string) => Promise<any>;
  register: (username: string, email: string, password: string, referralCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshCredits: () => Promise<void>;
  setupMFA: (tempToken?: string) => Promise<{ secret: string; qrCode: string }>;
  verifyMFA: (token: string, tempToken?: string) => Promise<{ message: string; backupCodes?: string[] }>;
  validateMFA: (tempToken: string, code: string) => Promise<void>;
  updateProfile: (data: { username?: string; profilePicture?: string }) => Promise<void>;
  changePassword: (data: { oldPassword: string; newPassword: string }) => Promise<void>;
  deleteAccount: (password: string) => Promise<void>;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Try to refresh the token first (in case of page reload)
        try {
          await authApiClient.refreshAccessToken();
        } catch (e) {
          // Ignore refresh error, user might just be logged out
        }

        // Check if we have a valid token and get user info
        if (authApiClient.isAuthenticated()) {
          const userData = await authApiClient.getCurrentUser();
          setUser(userData);
        }
      } catch (error) {
        console.error("Failed to initialize auth:", error);
        setUser(null);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, []);

  const refreshCredits = useCallback(async () => {
    if (!user) return;

    try {
      const credits = await creditApiClient.getCreditBalance();
      setUser(prevUser => prevUser ? { ...prevUser, credits } : null);
    } catch (error) {
      console.error('Failed to refresh credits:', error);
    }
  }, [user]);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await authApiClient.login(email, password);

      // Check for MFA requirement
      if ((data as any).mfaRequired) {
        return data; // Return the MFA data to the caller (LoginPage)
      }

      setUser(data.user);
      // Credits are included in login response, but we can refresh to be sure
      await refreshCredits();
      return data;
    } catch (err: any) {
      console.error('Login failed:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = async (token: string, referralCode?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await authApiClient.loginWithGoogle(token, referralCode);

      // Check for MFA requirement
      if ((data as any).mfaRequired || (data as any).mfaSetupRequired) {
        setIsLoading(false); // Stop loading here as we return to caller
        return data; // Return the MFA data to the caller (LoginPage)
      }

      setUser(data.user);
      await refreshCredits();
      return data;
    } catch (err: any) {
      console.error('Google login failed:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (username: string, email: string, password: string, referralCode?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await authApiClient.register(username, email, password, referralCode);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await authApiClient.logout();
      setUser(null);
    } catch (err: any) {
      console.error('Logout error:', err);
      // Still clear user state even if logout request fails
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const setupMFA = async (tempToken?: string) => {
    return await authApiClient.setupMFA(tempToken);
  };

  const verifyMFA = async (token: string, tempToken?: string) => {
    const result = await authApiClient.verifyMFA(token, tempToken);
    // If auto-login occurred, update user state
    if (result.user && result.token) {
      setUser(result.user);
      await refreshCredits();
    } else if (user) {
      // Fallback: manually update mfaEnabled status if no full user object returned
      setUser({ ...user, mfaEnabled: true });
    }
    return result;
  };

  const validateMFA = async (tempToken: string, code: string) => {
    setIsLoading(true);
    try {
      const data = await authApiClient.validateMFA(tempToken, code);
      setUser(data.user);
      await refreshCredits();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (data: { username?: string; profilePicture?: string }) => {
    setIsLoading(true);
    try {
      const updatedUser = await authApiClient.updateProfile(data);
      // Preserve existing user properties (like credits, role) while updating profile
      setUser(prev => prev ? { ...prev, ...updatedUser } : updatedUser);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (data: { oldPassword: string; newPassword: string }) => {
    setIsLoading(true);
    try {
      await authApiClient.changePassword(data);
      // Password changed successfully. We might want to logout or keep logged in.
      // Backend revokes tokens, so we should probably force logout or just handle 401 on next request.
      // But for better UX, let's just log them out explicitly or notify them.
      // The requirement usually implies re-login.
      await logout();
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAccount = async (password: string) => {
    setIsLoading(true);
    try {
      await authApiClient.deleteAccount(password);
      setUser(null);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    login,
    loginWithGoogle,
    register,
    logout,
    refreshCredits,
    setupMFA,
    verifyMFA,
    validateMFA,
    updateProfile,
    changePassword,
    deleteAccount,
    isLoading,
    isInitialized,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};